import React, { useState, useEffect } from 'react';
import AdminBaseLayout from '../../components/layouts/AdminBaseLayout';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import SearchIcon from '@mui/icons-material/Search';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import AdminHttpService from '../../api/AdminHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';

function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);

  const fetchLogs = async (pageNumber = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;
      const formattedStartDate = startDate && endDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
      const formattedEndDate = startDate && endDate ? dayjs(endDate).format('YYYY-MM-DD') : '';
      const response = await AdminHttpService.getLogs(pageNumber, searchTerm, formattedStartDate, formattedEndDate);
      setLogs(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total);
      setRowsPerPage(response.pagination.per_page);
    } catch (error) {
      setError(error.message || 'Erro ao buscar logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const [start, end] = dateRange;
      if ((start && !end) || (!start && end)) {
        return; // Don't fetch if only one date is selected
      }
      setPage(0); // Reset to first page when filters change
      fetchLogs(1, search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, dateRange]);

  useEffect(() => {
    fetchLogs(page + 1, search);
  }, [page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleDateChange = (index, newValue) => {
    setDateRange(prev => {
      const newRange = [...prev];
      newRange[index] = newValue;
      return newRange;
    });
  };

  return (
    <AdminBaseLayout>
      <LoadingOverlay open={loading} />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Paper elevation={3} sx={{ p: { xs: 0.5, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex',
          mb: 2,
          px: { xs: 1, sm: 0 }
        }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }}>
            Logs de Administradores
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            placeholder="Buscar por nome, email ou log"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1}
              sx={{ 
                minWidth: { xs: '100%', md: '400px' },
                '& .MuiTextField-root': { flex: 1 }
              }}
            >
              <DatePicker
                label="Data inicial"
                value={dateRange[0]}
                onChange={(newValue) => handleDateChange(0, newValue)}
                format="DD/MM/YYYY"
                maxDate={dateRange[1] || undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { 
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                      }
                    }
                  }
                }}
              />
              <DatePicker
                label="Data final"
                value={dateRange[1]}
                onChange={(newValue) => handleDateChange(1, newValue)}
                format="DD/MM/YYYY"
                minDate={dateRange[0] || undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { 
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                      }
                    }
                  }
                }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>

        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 400px)',
            width: '100%',
            overflowX: 'auto'
          }}>
            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Nome</TableCell>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Email</TableCell>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Log</TableCell>
                  <TableCell sx={{ 
                    whiteSpace: 'nowrap',
                    padding: { xs: '4px 2px', sm: '16px' },
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                  }}>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{log.admin_name}</TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{log.admin_email}</TableCell>
                    <TableCell sx={{ 
                      padding: { xs: '4px 2px', sm: '16px' },
                      fontSize: { xs: '0.75rem', sm: '1rem' }
                    }}>{log.log}</TableCell>
                    <TableCell
                      sx={{
                        padding: { xs: '4px 2px', sm: '16px' },
                        fontSize: { xs: '0.75rem', sm: '1rem' }
                      }}
                    >
                      {new Date(log.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            sx={{
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '1rem' }
              },
              '.MuiTablePagination-select': {
                fontSize: { xs: '0.75rem', sm: '1rem' }
              }
            }}
          />
        </Box>
      </Paper>
    </AdminBaseLayout>
  );
}

export default withAuthAdmin(AdminLogs);
